// src/components/KnowledgeBase.js
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, notification, Popconfirm } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal';
import GenericViewDrawer from '../components/GenericViewModal';
import Spinner from '../components/Loader';
import NavBar from '../components/NavBar';
import {
  fetchKbRequest,
  createKbRequest,
  updateKbRequest,
  deleteKbRequest,
} from '../redux-saga/actions/knowledgeBaseActions';
import { DeleteOutlined, EditOutlined, EyeOutlined,BookOutlined ,CreditCardOutlined} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

// Enum values
const STORAGE_TYPE = ['SIMPLE', 'SUMMARY', 'FULL_CONTEXT'];
const RETRIEVAL_TYPE = [
  'DEFAULT',
  'ITERATIVE_GENERATION',
  'ITERATIVE_RETRIEVAL',
];
const EMBEDDING_TYPE = [
  'BGE_DENSE',
  'BGE_COLBERT',
  'BGE_SPARSE',
  'OPENAI_SMALL_3',
  'OPENAI_LARGE_3',
  'OPENAI_ADA_2',
];

const KnowledgeBase = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const knowledgeBases = useSelector(
    (state) => state.knowledgeBase.data || [],
  );
  const loading = useSelector((state) => state.knowledgeBase.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentKb, setCurrentKb] = useState(null);
  

  useEffect(() => {
    dispatch(fetchKbRequest());
  }, [dispatch]);

  const handleCreateOrEdit = (values) => {
    const payload = {
    bucketName: String(values.bucketName),            // Ensure sequence is a number
    chunkSize: Number(values.chunkSize),                      // Ensure POA is a string
    topK: Number(values.topK),      // Ensure encounterId is a string
    name: String(values.name),   
    description: String(values.description), 
    model: String(values.model),        
    storageType: String(values.storageType), 
    retrievalType: String(values.retrievalType), 
    embeddingType: String(values.embeddingType),
                 
    };
    if (currentKb) {
      dispatch(updateKbRequest(currentKb.id, payload));
      notification.success({
        message: 'Updated',
        description: 'The knowledge base has been updated successfully.',
      });
    } else {
      dispatch(createKbRequest(payload));
      notification.success({
        message: 'Saved',
        description: 'The knowledge base has been saved successfully.',
      });
    }
    setIsModalVisible(false);
    setCurrentKb(null);
  };

  const handleDelete = (id) => {
    dispatch(deleteKbRequest(id));
    notification.success({
      message: 'Deleted',
      description: 'The knowledge base has been deleted successfully.',
    });
  };

  const handleEdit = (record) => {
    const {embeddingType,storageType , ...data}=record
    data['createdAt'] = moment(data.CreatedAt).format("YYYY-MM-DD HH:mm:ss");
    data['updatedAt'] = moment(data.UpdatedAt).format("YYYY-MM-DD HH:mm:ss");
    setCurrentKb(record);
    setIsModalVisible(true);
  };

  const handleView = (record) => {
    const {id , ...data}=record
    data['createdAt'] = moment(data.CreatedAt).format("YYYY-MM-DD HH:mm:ss");
    data['updatedAt'] = moment(data.UpdatedAt).format("YYYY-MM-DD HH:mm:ss");
    setCurrentKb(data);
    setIsDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
    setCurrentKb(null);
  };

  const handleDetailedView = (kbId) => {
    navigate(`/knowledge-base/${kbId}/details`);
  };

  const columnsConfig = [
    { title: 'Name', dataIndex: 'name', sortable: true },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Model', dataIndex: 'model' },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)} />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this knowledge base?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} />
          </Popconfirm>
          <Button onClick={() => handleDetailedView(record.id)}>
            Detailed View
          </Button>
        </div>
      ),
    },
  ];
  const modalInputs = [
    {
      label: 'Name',
      name: 'name',
      type: 'text',
      required: true,
      placeholder: 'Enter name',
    },
    {
      label: 'Description',
      name: 'description',
      type: 'text',
      required: true,
      placeholder: 'Enter description',
    },
    {
      label: 'Model',
      name: 'model',
      type: 'text',
      required: true,
      placeholder: 'Enter model type',
    },
    {
      label: 'Chunk Size',
      name: 'chunkSize',
      type: 'number',
      required: true,
      placeholder: 'Enter chunk size',
    },
    {
      label: 'Top K',
      name: 'topK',
      type: 'number',
      required: true,
      placeholder: 'Enter top K value',
      // Visible for all storage types, so no conditionalFields needed
    },
    {
      label: 'Content',
      name: 'content',
      type: 'text',
      placeholder: 'Enter content',
      conditionalFields: {
        dependsOn: "storageType",
        visibleFor: ["SUMMARY",]
      }
    },
    {
      label: 'L2 Chunk Size',
      name: 'l2ChunkSize',
      type: 'number',
      placeholder: 'Enter L2 chunk size',
      conditionalFields: {
        dependsOn: "storageType",
        visibleFor: ["FULL_CONTEXT"]
      }
    },
    {
      label: 'L2 Top K',
      name: 'l2TopK',
      type: 'number',
      placeholder: 'Enter L2 top K value',
      conditionalFields: {
        dependsOn: "storageType",
        visibleFor: [ "FULL_CONTEXT"]
      }
    },
    {
      label: 'Number of Iterations',
      name: 'noOfIteration',
      type: 'number',
      required: true,
      placeholder: 'Enter number of iterations',
      conditionalFields: {
        dependsOn: "retrievalType",
        visibleFor: [ 'ITERATIVE_GENERATION','ITERATIVE_RETRIEVAL']
      } 
    },
    {
      label: 'Retrieval Type',
      name: 'retrievalType',
      type: 'select',
      required: true,
      options: RETRIEVAL_TYPE.map((type) => ({
        label: type,
        value: type,
      })),
    },
    {
      label: 'Storage Type',
      name: 'storageType',
      type: 'select',
      required: true,
      options: STORAGE_TYPE.map((type) => ({
        label: type,
        value: type,
      })),
    },
    {
      label: 'Embedding Type',
      name: 'embeddingType',
      type: 'select',
      required: true,
      options: EMBEDDING_TYPE.map((type) => ({
        label: type,
        value: type,
      })),
    },
  ];
  

  return (
    <div style={{ display: 'flex' }}>
      <NavBar />
      <div style={{ width: '100%', padding: '10px' }}>
      <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
      <BookOutlined style={{ fontSize: "24px", color: "black" }} />
      <h2 style={{ margin: 0 }}>KnowledgeBase</h2>
    </div>

    {/* Create Project Button */}
    <div style={{ display: "flex", justifyContent: "end", marginBottom: 16 }}>
      <Button type="primary" onClick={() => setIsModalVisible(true)}>
        Create Project <CreditCardOutlined />
      </Button>
    </div>
    </div>
       
        <div
          style={{
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <GenericTable
              data={knowledgeBases}
              columnsConfig={columnsConfig}
              searchKeys={['name', 'description']}
            />
          )}
        </div>

        {/* Create/Edit Modal */}
        {isModalVisible && (
          <GenericModal
            isVisible={isModalVisible}
            handleCancel={() => setIsModalVisible(false)}
            title={
              <span>
                <BookOutlined style={{ color: "white", marginRight: "8px" }} />
                {currentKb ? "Edit Project" : "Create Project"}
              </span>
            }
            inputs={modalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={currentKb || {}}
          />
        )}

        {/* View Drawer */}
        {isDrawerVisible && currentKb && (
          <GenericViewDrawer
            isVisible={isDrawerVisible}
            handleCancel={handleDrawerClose}
            title="Knowledge Base Details"
            data={currentKb}
            footerButtons={['Close']}
            onButtonClick={handleDrawerClose}
          />
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
